/**
 * confirmCommand — finalize an active command.
 *
 * When the user presses Enter / clicks to commit, this module:
 *   1. If numeric input was typed, applies the translate/scale/rotate
 *      directly from the typed value (in edit or object mode). The mouse
 *      preview path is skipped by applyCommandLive when numericInput is set,
 *      so nothing has been applied yet — this is the real commit path.
 *   2. Re-computes normals & UVs (deferred from the drag path for
 *      performance on large meshes) and syncs baseVertices.
 *   3. Tears down IK sessions.
 *   4. Captures a `lastOperation` record for the Redo panel (parameterized
 *      ops only — translate/rotate/scale/inset/bevel/… snapshot + params).
 *   5. Clears commandBuffer + commandPreSnapshot + commandCancelSnapshot.
 */

import * as THREE from "three/webgpu";
import { recordCommandConfirm } from "../../debug/listenerProbe.ts";
import { destroyIKSession } from "../../mannequin/ikStabilizer.ts";
import { destroyWorkerSession } from "../../mannequin/ikWorkerClient.ts";
import {
  applyCurveEditVertices,
  applyHandleConstraintsToEditVerts,
  buildCurveEditVertices,
} from "../../modeling/curveOps.ts";
import {
  recomputeLoopNormals as meshRecomputeLoopNormals,
  recomputeLoopNormalsPartial,
  resolveAutoSmoothAngle,
} from "../../modeling/meshOps.ts";
import {
  applyMirrorClipping,
  collectClipPlanes,
} from "../../modeling/mirrorClipping.ts";
import {
  getCommandMetadata,
  getParamDef,
  getPrimaryParam,
  usesTopologyPreview,
} from "../../modeling/operatorParams.ts";
import {
  computePivot,
  rotatePointFromPivot,
  scalePointFromPivot,
  usesIndividualOrigins,
} from "../../modeling/pivot.ts";
import { setLastSnapResult } from "../../modeling/snap.ts";
import type { SceneObject } from "../../modeling/types.ts";
import { cloneLoopNormals } from "../../modeling/types.ts";
import {
  unwrapBoxProject,
  unwrapBoxProjectPartial,
} from "../../modeling/uvUnwrap.ts";
import { getCollectionForObject } from "../collectionGraph.ts";
import { regenerateCurveGeometryInMap } from "../curveRegen.ts";
import { materializeEditSessionPatch } from "../editSession.ts";
import {
  clearLiveEditPreview,
  getLiveEditPreviewForObject,
} from "../liveEditPreview.ts";
import { patchSceneObject } from "../sceneObjectMap.ts";
import { patchSceneObjectAfterEdit } from "../storeEditOps/index.ts";
import type {
  CommandType,
  ObjectGeometrySnapshotDelta,
  VertexPositionDelta,
} from "../storeTypes.ts";
import {
  captureEditSelectionSnapshot,
  captureObjectGeometryHistoryState,
  commandSnapshotObject,
  isEditObjectSnapshot,
  isFullSnapshot,
  undoManager,
} from "../storeUndo.ts";
import {
  notifyTopologyOpFailure,
  type TopologyOpFailureId,
} from "../topologyOpNotifications.ts";
import { cancelCommand } from "./commandLifecycle.ts";
import {
  _scratchMat4B,
  _scratchVec3C,
  _scratchVec3D,
  type Get_,
  releaseLiveVertsScratch,
  type Set_,
} from "./core.ts";
import { captureGeometrySnapshot } from "./replayStrategies.ts";
import { executeTopologyOp, snapToTopology } from "./topologyExecute.ts";
import {
  applyEditComponentTransform,
  buildEditTransformComponents,
  collectSnapshotTransformTargets,
  computeObjectPivot,
  computeOrientedObjectScale,
  computeRotatedObjectPosition,
  computeScaledObjectPosition,
  cursorInObjectLocal,
} from "./transformMath.ts";
import { applyObjectTransformPatch } from "./transformTargets.ts";

/**
 * Union of explicitly selected vertices and proportional-edit falloff keys —
 * the authoritative "vertices this command may have moved" set for the
 * partial-recompute fast path in confirmCommand. Mirror clipping does not
 * introduce additional vertices (it only clamps vertices already in one of
 * these sets).
 *
 * Returns `null` when the union would cover nothing or when we can't form
 * a bounded set (no selection at all). Callers must fall back to full
 * recompute in that case.
 */
function collectAffectedVertices(
  selected: ReadonlySet<number>,
  proportionalWeights: ReadonlyMap<number, number> | null | undefined,
): Set<number> | null {
  if (
    selected.size === 0 &&
    (!proportionalWeights || proportionalWeights.size === 0)
  ) {
    return null;
  }
  const out = new Set<number>(selected);
  if (proportionalWeights) {
    for (const vi of proportionalWeights.keys()) out.add(vi);
  }
  return out;
}

function packedVertexPositions(
  vertices: Float32Array,
  affected: Uint32Array,
): Float32Array {
  const out = new Float32Array(affected.length * 3);
  for (let i = 0; i < affected.length; i++) {
    const vi = affected[i]!;
    const src = vi * 3;
    const dst = i * 3;
    out[dst] = vertices[src]!;
    out[dst + 1] = vertices[src + 1]!;
    out[dst + 2] = vertices[src + 2]!;
  }
  return out;
}

function packedPositionsEqual(a: Float32Array, b: Float32Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function buildVertexPositionHistoryEntry(
  before: NonNullable<ReturnType<typeof commandSnapshotObject>>,
  beforeSelection: NonNullable<ReturnType<typeof captureEditSelectionSnapshot>>,
  after: SceneObject,
  afterSelection: NonNullable<ReturnType<typeof captureEditSelectionSnapshot>>,
  affectedSet: ReadonlySet<number>,
): VertexPositionDelta | null {
  if (affectedSet.size === 0) return null;
  const affected = Uint32Array.from([...affectedSet].sort((a, b) => a - b));
  const beforeVertices = packedVertexPositions(before.vertices, affected);
  const afterVertices = packedVertexPositions(after.vertices, affected);
  const hasBaseVertices = Boolean(
    before.baseVertices &&
      after.baseVertices &&
      before.baseVertices.length === before.vertices.length &&
      after.baseVertices.length === after.vertices.length,
  );
  const beforeBaseVertices = hasBaseVertices
    ? packedVertexPositions(before.baseVertices!, affected)
    : undefined;
  const afterBaseVertices = hasBaseVertices
    ? packedVertexPositions(after.baseVertices!, affected)
    : undefined;
  if (
    packedPositionsEqual(beforeVertices, afterVertices) &&
    (!beforeBaseVertices ||
      !afterBaseVertices ||
      packedPositionsEqual(beforeBaseVertices, afterBaseVertices))
  ) {
    return null;
  }
  const entry: VertexPositionDelta = {
    type: "vertex-position-delta",
    objectId: before.id,
    affected,
    beforeVertices,
    afterVertices,
    beforeEditSelection: beforeSelection,
    afterEditSelection: afterSelection,
  };
  if (beforeBaseVertices && afterBaseVertices) {
    entry.beforeBaseVertices = beforeBaseVertices;
    entry.afterBaseVertices = afterBaseVertices;
  }
  return entry;
}

function buildObjectGeometryHistoryEntry(
  before: NonNullable<ReturnType<typeof commandSnapshotObject>>,
  beforeSelection: NonNullable<ReturnType<typeof captureEditSelectionSnapshot>>,
  selectedObjectTargets: ObjectGeometrySnapshotDelta["before"]["selectedObjectTargets"],
  get: Get_,
): ObjectGeometrySnapshotDelta | null {
  const after = captureObjectGeometryHistoryState(get(), before.id);
  if (!after) return null;
  return {
    type: "object-geometry-snapshot",
    objectId: before.id,
    before: {
      object: before,
      editSelection: beforeSelection,
      selectedObjectTargets: selectedObjectTargets.map((target) => ({
        ...target,
      })),
    },
    after,
  };
}

function topologyFailureIdForCommand(
  type: CommandType,
): TopologyOpFailureId | null {
  switch (type) {
    case "extrude":
      return "extrude";
    case "subdivide":
      return "subdivide";
    case "inset":
      return "inset";
    case "bevel":
      return "bevel";
    case "bridgeEdgeLoops":
      return "bridge_edge_loops";
    case "gridFill":
      return "grid_fill";
    case "mergeByDistance":
      return "merge_by_distance";
    case "edgeLoopInsert":
      return "edge_loop_insert";
    default:
      return null;
  }
}

function resolveTopologyConfirmParams(
  cb: NonNullable<ReturnType<Get_>["commandBuffer"]>,
): Record<string, number | boolean | string> {
  if (!cb.numericInput) return cb.params;
  const activeKey = cb.activeValueMode ?? getPrimaryParam(cb.type)?.key;
  const paramDef = activeKey ? getParamDef(cb.type, activeKey) : null;
  if (!paramDef || (paramDef.type !== "float" && paramDef.type !== "int")) {
    return cb.params;
  }
  const raw = cb.numericInput.trim();
  const value = raw === "" || raw === "-" ? 0 : parseFloat(raw);
  if (!Number.isFinite(value)) return cb.params;
  const clamped = Math.max(paramDef.min, Math.min(paramDef.max, value));
  return {
    ...cb.params,
    [paramDef.key]: paramDef.type === "int" ? Math.round(clamped) : clamped,
  };
}

export function confirmCommand(set: Set_, get: Get_): void {
  const state = get();
  const cb = state.commandBuffer;
  if (!cb) return;

  const inEditMode = state.mode === "edit" && state.editMode != null;
  const commandPreSnapshot = state.commandPreSnapshot;
  const editObjectCommandSnapshot = isEditObjectSnapshot(commandPreSnapshot)
    ? commandPreSnapshot
    : null;
  const editHistoryBeforeObject =
    inEditMode && commandPreSnapshot && state.editMode
      ? commandSnapshotObject(commandPreSnapshot, state.editMode.objectId)
      : null;
  const editHistoryBeforeSelection = editObjectCommandSnapshot?.editSelection;
  let confirmedParams = cb.params;

  if (inEditMode && usesTopologyPreview(cb.type)) {
    const em = state.editMode!;
    const snap = state.commandPreSnapshot;
    const obj = state.sceneObjects.get(em.objectId);
    const snapObj = snap ? commandSnapshotObject(snap, em.objectId) : null;
    if (!snap || !obj || !snapObj) {
      cancelCommand(set, get);
      return;
    }

    confirmedParams = resolveTopologyConfirmParams(cb);
    const result = executeTopologyOp(
      cb.type,
      confirmedParams,
      new Float32Array(snapObj.vertices),
      snapObj,
      em,
      obj,
      state,
      set,
      {
        preEditFaces: cb.preEditFaces,
        preEditSelection: cb.preEditSelection,
        preEditEdges: cb.preEditEdges,
        preEditActiveVertex: cb.preEditActiveVertex,
        preEditActiveEdge: cb.preEditActiveEdge,
        preEditActiveFace: cb.preEditActiveFace,
        edgeLoopInsertEdge: cb.edgeLoopInsertEdge,
      },
    );
    if (!result.ok) {
      const failureId = topologyFailureIdForCommand(cb.type);
      if (failureId) notifyTopologyOpFailure(failureId, result.reason);
      cancelCommand(set, get);
      return;
    }
    if (confirmedParams !== cb.params) {
      set({ commandBuffer: { ...cb, params: confirmedParams } });
    }
  }

  if (inEditMode && !usesTopologyPreview(cb.type)) {
    const current = get();
    const em = current.editMode;
    const liveEditPreview = em
      ? getLiveEditPreviewForObject(em.objectId)
      : null;
    const activeEditSession = current.activeEditSession;
    let confirmCommitSource: Parameters<typeof recordCommandConfirm>[0] =
      "none";
    if (em && liveEditPreview) {
      const obj = current.sceneObjects.get(em.objectId);
      if (!obj) return;
      confirmCommitSource = "livePreview";
      set({
        ...patchSceneObjectAfterEdit(
          current,
          em.objectId,
          materializeEditSessionPatch(obj, liveEditPreview),
        ),
        activeEditSession: null,
      });
      clearLiveEditPreview(em.objectId);
    } else if (em && activeEditSession?.objectId === em.objectId) {
      const obj = current.sceneObjects.get(em.objectId);
      if (!obj) return;
      confirmCommitSource = "activeEditSession";
      set({
        ...patchSceneObjectAfterEdit(
          current,
          em.objectId,
          materializeEditSessionPatch(obj, activeEditSession),
        ),
        activeEditSession: null,
      });
    }
    recordCommandConfirm(confirmCommitSource);
  } else {
    recordCommandConfirm("none");
  }

  // If the user typed numeric input, apply it now — applyCommandLive skips
  // vertex updates when numericInput is set, so nothing was applied yet.
  // If no numeric input and mouse was used, the live preview already applied.
  if (cb.numericInput && !usesTopologyPreview(cb.type)) {
    if (inEditMode) {
      // ---------- Edit mode: apply numeric transform to selected vertices ----------
      const em = state.editMode!;
      const snap = state.commandPreSnapshot;
      const obj = state.sceneObjects.get(em.objectId);
      if (
        snap &&
        obj &&
        (cb.type === "translate" || cb.type === "scale" || cb.type === "rotate")
      ) {
        const raw = cb.numericInput.trim();
        const value = raw === "" || raw === "-" ? 0 : parseFloat(raw);
        if (Number.isFinite(value)) {
          if (em.isCurve && obj.curveData) {
            // --- Curve branch ---
            const snapObj = commandSnapshotObject(snap, em.objectId);
            if (snapObj?.curveData) {
              const srcEditVerts = buildCurveEditVertices(snapObj.curveData);
              const newEditVerts = new Float32Array(srcEditVerts);

              const pivotPositions: [number, number, number][] = [];
              for (const vi of em.selectedVertices) {
                const pi = Math.floor(vi / 3);
                const comp = vi % 3;
                const base = pi * 9 + comp * 3;
                pivotPositions.push([
                  srcEditVerts[base],
                  srcEditVerts[base + 1],
                  srcEditVerts[base + 2],
                ]);
              }
              const [px, py, pz] = computePivot(
                pivotPositions,
                state.pivotReference,
                cursorInObjectLocal(
                  obj,
                  getCollectionForObject(state.collections, em.objectId),
                  state.cursor3D.position,
                ),
              );
              const individualOrigins = usesIndividualOrigins(
                state.pivotReference,
              );

              if (cb.type === "translate" && cb.axis) {
                if (cb.axis === "normal" && cb.normalVector) {
                  const [nvx, nvy, nvz] = cb.normalVector;
                  for (const vi of em.selectedVertices) {
                    const pi = Math.floor(vi / 3);
                    const comp = vi % 3;
                    const base = pi * 9 + comp * 3;
                    newEditVerts[base] = srcEditVerts[base] + nvx * value;
                    newEditVerts[base + 1] =
                      srcEditVerts[base + 1] + nvy * value;
                    newEditVerts[base + 2] =
                      srcEditVerts[base + 2] + nvz * value;
                  }
                } else if (cb.axis !== "normal") {
                  const ai = cb.axis === "x" ? 0 : cb.axis === "y" ? 1 : 2;
                  for (const vi of em.selectedVertices) {
                    const pi = Math.floor(vi / 3);
                    const comp = vi % 3;
                    const base = pi * 9 + comp * 3;
                    newEditVerts[base + ai] = srcEditVerts[base + ai] + value;
                  }
                }
              } else if (cb.type === "scale") {
                if (individualOrigins) {
                  // Each selected curve element scales around itself, so positions stay fixed.
                } else {
                  const factor = value;
                  for (const vi of em.selectedVertices) {
                    const pi = Math.floor(vi / 3);
                    const comp = vi % 3;
                    const base = pi * 9 + comp * 3;
                    if (cb.axis === "normal" && cb.normalVector) {
                      const [nvx, nvy, nvz] = cb.normalVector;
                      const nvLen2 = nvx * nvx + nvy * nvy + nvz * nvz;
                      if (nvLen2 > 1e-12) {
                        const ox = srcEditVerts[base] - px;
                        const oy = srcEditVerts[base + 1] - py;
                        const oz = srcEditVerts[base + 2] - pz;
                        const dot = (ox * nvx + oy * nvy + oz * nvz) / nvLen2;
                        newEditVerts[base] = px + ox + (factor - 1) * dot * nvx;
                        newEditVerts[base + 1] =
                          py + oy + (factor - 1) * dot * nvy;
                        newEditVerts[base + 2] =
                          pz + oz + (factor - 1) * dot * nvz;
                      }
                    } else if (cb.axis && cb.axis !== "normal") {
                      const ai = cb.axis === "x" ? 0 : cb.axis === "y" ? 1 : 2;
                      for (let c = 0; c < 3; c++) {
                        const f = c === ai ? factor : 1;
                        const pv = c === 0 ? px : c === 1 ? py : pz;
                        newEditVerts[base + c] =
                          pv + (srcEditVerts[base + c] - pv) * f;
                      }
                    } else {
                      newEditVerts[base] =
                        px + (srcEditVerts[base] - px) * factor;
                      newEditVerts[base + 1] =
                        py + (srcEditVerts[base + 1] - py) * factor;
                      newEditVerts[base + 2] =
                        pz + (srcEditVerts[base + 2] - pz) * factor;
                    }
                  }
                }
              } else if (cb.type === "rotate") {
                if (individualOrigins) {
                  // Each selected curve element rotates around itself, so positions stay fixed.
                } else {
                  const rad = (value * Math.PI) / 180;
                  let axis: THREE.Vector3;
                  if (cb.axis === "normal" && cb.normalVector) {
                    axis = _scratchVec3C
                      .set(
                        cb.normalVector[0],
                        cb.normalVector[1],
                        cb.normalVector[2],
                      )
                      .normalize();
                  } else if (cb.axis && cb.axis !== "normal") {
                    axis = _scratchVec3C.set(
                      cb.axis === "x" ? 1 : 0,
                      cb.axis === "y" ? 1 : 0,
                      cb.axis === "z" ? 1 : 0,
                    );
                  } else {
                    axis = _scratchVec3C.set(0, 0, 1); // default to Z
                  }
                  const rotMat = _scratchMat4B.makeRotationAxis(axis, rad);
                  const v = _scratchVec3D;
                  for (const vi of em.selectedVertices) {
                    const pi = Math.floor(vi / 3);
                    const comp = vi % 3;
                    const base = pi * 9 + comp * 3;
                    v.set(
                      srcEditVerts[base] - px,
                      srcEditVerts[base + 1] - py,
                      srcEditVerts[base + 2] - pz,
                    );
                    v.applyMatrix4(rotMat);
                    newEditVerts[base] = v.x + px;
                    newEditVerts[base + 1] = v.y + py;
                    newEditVerts[base + 2] = v.z + pz;
                  }
                }
              }

              applyHandleConstraintsToEditVerts(
                newEditVerts,
                snapObj.curveData,
                em.selectedVertices,
              );
              const updatedCurveData = applyCurveEditVertices(
                snapObj.curveData,
                newEditVerts,
              );
              const objects = regenerateCurveGeometryInMap(
                state.sceneObjects,
                em.objectId,
                updatedCurveData,
              );
              set({
                sceneObjects: objects,
                editMode: { ...em, curveEditVertices: newEditVerts },
              });
            }
          } else {
            // --- Mesh branch ---
            const snapObj = commandSnapshotObject(snap, em.objectId);
            if (snapObj) {
              const srcVerts = snapObj.vertices;
              const newVerts = new Float32Array(srcVerts);

              const pivotPositions: [number, number, number][] = [];
              for (const vi of em.selectedVertices) {
                pivotPositions.push([
                  srcVerts[vi * 3],
                  srcVerts[vi * 3 + 1],
                  srcVerts[vi * 3 + 2],
                ]);
              }
              const [px, py, pz] = computePivot(
                pivotPositions,
                state.pivotReference,
                cursorInObjectLocal(
                  obj,
                  getCollectionForObject(state.collections, em.objectId),
                  state.cursor3D.position,
                ),
              );
              const individualOrigins = usesIndividualOrigins(
                state.pivotReference,
              );
              const individualComponents = individualOrigins
                ? buildEditTransformComponents(
                    srcVerts,
                    snapToTopology(snapObj),
                    em,
                  )
                : null;

              if (cb.type === "translate" && cb.axis) {
                if (cb.axis === "normal" && cb.normalVector) {
                  const [nvx, nvy, nvz] = cb.normalVector;
                  for (const vi of em.selectedVertices) {
                    newVerts[vi * 3] = srcVerts[vi * 3] + nvx * value;
                    newVerts[vi * 3 + 1] = srcVerts[vi * 3 + 1] + nvy * value;
                    newVerts[vi * 3 + 2] = srcVerts[vi * 3 + 2] + nvz * value;
                  }
                } else if (cb.axis !== "normal") {
                  const ai = cb.axis === "x" ? 0 : cb.axis === "y" ? 1 : 2;
                  for (const vi of em.selectedVertices) {
                    newVerts[vi * 3 + ai] = srcVerts[vi * 3 + ai] + value;
                  }
                }
              } else if (cb.type === "scale") {
                const factor = value;
                if (individualComponents) {
                  applyEditComponentTransform(
                    srcVerts,
                    newVerts,
                    individualComponents,
                    null,
                    (point, pivot) =>
                      scalePointFromPivot(
                        point,
                        pivot,
                        factor,
                        cb.axis && cb.axis !== "normal" ? cb.axis : null,
                        cb.axis === "normal" ? cb.normalVector : null,
                      ),
                  );
                } else {
                  for (const vi of em.selectedVertices) {
                    const next = scalePointFromPivot(
                      [
                        srcVerts[vi * 3],
                        srcVerts[vi * 3 + 1],
                        srcVerts[vi * 3 + 2],
                      ],
                      [px, py, pz],
                      factor,
                      cb.axis && cb.axis !== "normal" ? cb.axis : null,
                      cb.axis === "normal" ? cb.normalVector : null,
                    );
                    newVerts[vi * 3] = next[0];
                    newVerts[vi * 3 + 1] = next[1];
                    newVerts[vi * 3 + 2] = next[2];
                  }
                }
              } else if (cb.type === "rotate") {
                const rad = (value * Math.PI) / 180;
                let axis: THREE.Vector3;
                if (cb.axis === "normal" && cb.normalVector) {
                  axis = _scratchVec3C
                    .set(
                      cb.normalVector[0],
                      cb.normalVector[1],
                      cb.normalVector[2],
                    )
                    .normalize();
                } else if (cb.axis && cb.axis !== "normal") {
                  axis = _scratchVec3C.set(
                    cb.axis === "x" ? 1 : 0,
                    cb.axis === "y" ? 1 : 0,
                    cb.axis === "z" ? 1 : 0,
                  );
                } else {
                  axis = _scratchVec3C.set(0, 0, 1); // default to Z
                }
                const axisTuple: [number, number, number] = [
                  axis.x,
                  axis.y,
                  axis.z,
                ];
                if (individualComponents) {
                  applyEditComponentTransform(
                    srcVerts,
                    newVerts,
                    individualComponents,
                    null,
                    (point, pivot) =>
                      rotatePointFromPivot(point, pivot, axisTuple, rad),
                  );
                } else {
                  for (const vi of em.selectedVertices) {
                    const next = rotatePointFromPivot(
                      [
                        srcVerts[vi * 3],
                        srcVerts[vi * 3 + 1],
                        srcVerts[vi * 3 + 2],
                      ],
                      [px, py, pz],
                      axisTuple,
                      rad,
                    );
                    newVerts[vi * 3] = next[0];
                    newVerts[vi * 3 + 1] = next[1];
                    newVerts[vi * 3 + 2] = next[2];
                  }
                }
              }

              // Mirror clipping
              const clipPlanes = collectClipPlanes(
                [],
                state.modifierOverrides.get(em.objectId) ?? [],
              );
              if (clipPlanes.length > 0) {
                applyMirrorClipping(
                  newVerts,
                  clipPlanes,
                  em.selectedVertices,
                  srcVerts,
                );
              }

              // Keep the sculpt reference mesh in sync: shift baseVertices by
              // the same per-vertex delta applied to vertices so later sculpt
              // strokes still measure displacement against the edited base.
              // Only affected indices are dirty — the numeric-input path
              // writes nothing outside `em.selectedVertices`, and no
              // proportional edit runs here.
              const vertsPatch: Partial<SceneObject> = { vertices: newVerts };
              if (
                snapObj.baseVertices &&
                snapObj.baseVertices.length === srcVerts.length
              ) {
                const newBase = new Float32Array(snapObj.baseVertices);
                for (const vi of em.selectedVertices) {
                  const b = vi * 3;
                  newBase[b] += newVerts[b] - srcVerts[b];
                  newBase[b + 1] += newVerts[b + 1] - srcVerts[b + 1];
                  newBase[b + 2] += newVerts[b + 2] - srcVerts[b + 2];
                }
                vertsPatch.baseVertices = newBase;
              }
              // Destructive vertex edit — bakes any generator ref
              // (including a compound owner's collection ref) as
              // part of the patch.
              set(patchSceneObjectAfterEdit(state, em.objectId, vertsPatch));
            }
          }
        }
      }
    } else {
      const snap = state.commandPreSnapshot;
      if (snap) {
        if (!isFullSnapshot(snap)) return;
        const targets = collectSnapshotTransformTargets(
          snap,
          state.selectedObjectTargets,
        );
        const pivot = computeObjectPivot(
          targets,
          state.pivotReference,
          state.cursor3D.position,
        );
        const individualOrigins = usesIndividualOrigins(state.pivotReference);
        const raw = cb.numericInput.trim();

        if (cb.type === "translate") {
          const value = raw === "" || raw === "-" ? 0 : parseFloat(raw);
          if (Number.isFinite(value)) {
            let moveDir: [number, number, number] = [0, 0, 0];
            if (cb.axis === "normal" && cb.normalVector) {
              moveDir = [
                cb.normalVector[0] * value,
                cb.normalVector[1] * value,
                cb.normalVector[2] * value,
              ];
            } else if (cb.axis && cb.axis !== "normal") {
              moveDir = [
                cb.axis === "x" ? value : 0,
                cb.axis === "y" ? value : 0,
                cb.axis === "z" ? value : 0,
              ];
            }
            applyObjectTransformPatch(set, get, state, targets, (target) => ({
              position: [
                target.position[0] + moveDir[0],
                target.position[1] + moveDir[1],
                target.position[2] + moveDir[2],
              ],
            }));
          }
        } else if (cb.type === "scale") {
          const factor = raw === "" || raw === "-" ? 1 : parseFloat(raw);
          if (Number.isFinite(factor)) {
            applyObjectTransformPatch(set, get, state, targets, (target) => {
              const rotationQuat =
                target.kind === "scene"
                  ? new THREE.Quaternion().setFromEuler(
                      new THREE.Euler(...target.rotationEuler),
                    )
                  : new THREE.Quaternion(...target.rotationQuat);
              return {
                position: individualOrigins
                  ? target.position
                  : computeScaledObjectPosition(
                      target.position,
                      pivot,
                      factor,
                      cb.axis,
                      cb.normalVector,
                    ),
                scale: computeOrientedObjectScale(
                  target.scale,
                  factor,
                  cb.axis,
                  cb.normalVector,
                  rotationQuat,
                ),
              };
            });
          }
        } else if (cb.type === "rotate") {
          const degrees = raw === "" || raw === "-" ? 0 : parseFloat(raw);
          if (Number.isFinite(degrees)) {
            const angle = (degrees * Math.PI) / 180;
            const rotAxis =
              cb.axis === "normal" && cb.normalVector
                ? new THREE.Vector3(...cb.normalVector).normalize()
                : cb.axis && cb.axis !== "normal"
                  ? new THREE.Vector3(
                      cb.axis === "x" ? 1 : 0,
                      cb.axis === "y" ? 1 : 0,
                      cb.axis === "z" ? 1 : 0,
                    )
                  : new THREE.Vector3(0, 0, 1);
            const axisTuple: [number, number, number] = [
              rotAxis.x,
              rotAxis.y,
              rotAxis.z,
            ];
            const deltaQuat = new THREE.Quaternion().setFromAxisAngle(
              rotAxis,
              angle,
            );
            applyObjectTransformPatch(set, get, state, targets, (target) => {
              if (target.kind === "scene") {
                const snapEuler = new THREE.Euler(...target.rotationEuler);
                const snapQuat = new THREE.Quaternion().setFromEuler(snapEuler);
                const nextQuat = deltaQuat
                  .clone()
                  .multiply(snapQuat)
                  .normalize();
                const nextEuler = new THREE.Euler().setFromQuaternion(
                  nextQuat,
                  snapEuler.order,
                );
                return {
                  position: individualOrigins
                    ? target.position
                    : computeRotatedObjectPosition(
                        target.position,
                        pivot,
                        axisTuple,
                        angle,
                      ),
                  rotationEuler: [nextEuler.x, nextEuler.y, nextEuler.z],
                };
              }

              const snapQuat = new THREE.Quaternion(...target.rotationQuat);
              const nextQuat = deltaQuat.clone().multiply(snapQuat).normalize();
              return {
                position: individualOrigins
                  ? target.position
                  : computeRotatedObjectPosition(
                      target.position,
                      pivot,
                      axisTuple,
                      angle,
                    ),
                rotationQuat: [nextQuat.x, nextQuat.y, nextQuat.z, nextQuat.w],
              };
            });
          }
        }
      }
    }
  }

  // Curve post-drag: regenerate tube topology / loopNormals / UVs now that
  // the command is finalized. The live drag path updates vertex positions
  // only (see applyLiveCurveEdit), so shading / UVs / edge tables are stale
  // until this runs. Skipped for the numeric-input path, which already called
  // the regeneration helper directly in the block above.
  if (inEditMode && state.editMode?.isCurve && !cb.numericInput) {
    const current = get();
    set({
      sceneObjects: regenerateCurveGeometryInMap(
        current.sceneObjects,
        state.editMode.objectId,
      ),
    });
  }

  setLastSnapResult(null);
  destroyIKSession();
  destroyWorkerSession();
  releaseLiveVertsScratch();

  // Capture last operation for redo panel (only for parameterized ops with a
  // registered replay strategy). Modal commands always go through the
  // geometry-snapshot strategy — no other replay kind originates here.
  const meta = getCommandMetadata(cb.type);
  let lastOp = null;
  if (meta.params.length > 0 && meta.replayKind === "geometry-snapshot") {
    const capture = captureGeometrySnapshot(
      cb,
      commandPreSnapshot,
      state.editMode,
    );
    if (capture) {
      lastOp = {
        type: cb.type,
        params: { ...confirmedParams },
        replayKind: meta.replayKind,
        capture,
      };
    }
  }

  // Tear down command-only state before the post-confirm mesh rebuild. On
  // mobile, keeping the pre-snapshot, live-edit session, and scratch buffers
  // alive during the normals/UV/baseVertices clone path spikes peak memory
  // right at pointer-up, which is exactly when the crash was observed.
  clearLiveEditPreview();
  set({
    activeEditSession: null,
    commandBuffer: null,
    commandPreSnapshot: null,
    commandCancelSnapshot: null,
    pendingAxis: null,
    ikDragState: null,
    lastOperation: lastOp,
  });

  // Recompute normals now that the command is finalized (deferred from live drag).
  // Runs after command teardown so transient drag state can be collected before
  // we allocate replacement loopNormals / UV / baseVertices buffers.
  if (inEditMode && state.editMode && !state.editMode.isCurve) {
    const current = get();
    const em = current.editMode!;
    const obj = current.sceneObjects.get(em.objectId);
    if (obj) {
      const angle = resolveAutoSmoothAngle(obj.shading, obj.autoSmoothAngle);
      const snapObj = commandPreSnapshot
        ? commandSnapshotObject(commandPreSnapshot, em.objectId)
        : null;
      const preVerts = snapObj?.vertices ?? null;
      const preBase = snapObj?.baseVertices ?? null;

      const affected = collectAffectedVertices(
        em.selectedVertices,
        cb.proportionalWeights,
      );
      const vertexCount = obj.vertices.length / 3;
      const partial =
        affected !== null &&
        affected.size > 0 &&
        affected.size <= vertexCount * 0.5 &&
        obj.loopNormals.length === obj.topology.corners.length * 3;

      const patch: Record<string, unknown> = {};
      if (partial) {
        const affectedArr = Array.from(affected);
        const newNormals = cloneLoopNormals(obj.loopNormals);
        recomputeLoopNormalsPartial(
          obj.vertices,
          newNormals,
          affectedArr,
          obj.topology,
          angle,
        );
        patch.loopNormals = newNormals;
        if (obj.uvs && obj.uvs.length === obj.topology.corners.length * 2) {
          const newUvs = new Float32Array(obj.uvs);
          unwrapBoxProjectPartial(
            obj.vertices,
            newNormals,
            obj.topology,
            affected,
            newUvs,
          );
          patch.uvs = newUvs;
        }
        if (
          obj.baseVertices &&
          preBase &&
          preVerts &&
          preBase.length === obj.vertices.length &&
          preVerts.length === obj.vertices.length
        ) {
          const newBase = new Float32Array(preBase);
          for (const vi of affected) {
            const b = vi * 3;
            newBase[b] += obj.vertices[b] - preVerts[b];
            newBase[b + 1] += obj.vertices[b + 1] - preVerts[b + 1];
            newBase[b + 2] += obj.vertices[b + 2] - preVerts[b + 2];
          }
          patch.baseVertices = newBase;
        }
      } else {
        const loopNormals = meshRecomputeLoopNormals(
          obj.vertices,
          obj.topology,
          angle,
        );
        const uvs = unwrapBoxProject(obj.vertices, loopNormals, obj.topology);
        patch.loopNormals = loopNormals;
        patch.uvs = uvs;
        if (
          obj.baseVertices &&
          preBase &&
          preVerts &&
          preBase.length === obj.vertices.length &&
          preVerts.length === obj.vertices.length
        ) {
          const newBase = new Float32Array(preBase);
          for (let i = 0; i < newBase.length; i++)
            newBase[i] += obj.vertices[i] - preVerts[i];
          patch.baseVertices = newBase;
        }
      }
      set({
        sceneObjects: patchSceneObject(
          current.sceneObjects,
          em.objectId,
          patch,
        ),
      });
    }
  }

  if (
    inEditMode &&
    editObjectCommandSnapshot &&
    editHistoryBeforeObject &&
    editHistoryBeforeSelection
  ) {
    const current = get();
    const em = current.editMode;
    const afterObj = em ? current.sceneObjects.get(em.objectId) : null;
    let historyEntry: VertexPositionDelta | ObjectGeometrySnapshotDelta | null =
      null;

    const objectSnapshotCommand =
      usesTopologyPreview(cb.type) ||
      cb.type === "skinRadius" ||
      editHistoryBeforeSelection.isCurve === true;

    if (objectSnapshotCommand) {
      historyEntry = buildObjectGeometryHistoryEntry(
        editHistoryBeforeObject,
        editHistoryBeforeSelection,
        editObjectCommandSnapshot.selectedObjectTargets,
        get,
      );
    } else if (
      afterObj &&
      em &&
      (cb.type === "translate" ||
        cb.type === "rotate" ||
        cb.type === "scale" ||
        cb.type === "edgeSlide" ||
        cb.type === "shrinkFatten")
    ) {
      const affected = collectAffectedVertices(
        editHistoryBeforeSelection.selectedVertices,
        cb.proportionalWeights,
      );
      if (
        affected &&
        editHistoryBeforeObject.vertices.length === afterObj.vertices.length
      ) {
        historyEntry = buildVertexPositionHistoryEntry(
          editHistoryBeforeObject,
          editHistoryBeforeSelection,
          afterObj,
          captureEditSelectionSnapshot(em),
          affected,
        );
      }
    }

    if (historyEntry) {
      undoManager.push(historyEntry);
      set({ canUndo: undoManager.canUndo, canRedo: undoManager.canRedo });
    }
  }
}
